import { getKeyManager } from '../crypto/keys';
import { getCryptoBox } from '../crypto/box';
import { getDatabase, LedgerMetadata } from './database';
import { LedgerDocument } from '../crdt/ledgerDoc';
import { 
  Household, 
  Member, 
  Account, 
  Category, 
  Transaction, 
  BudgetAllocation, 
  Schedule,
  UUID 
} from '../types';

// Encrypted local-first storage implementation
export class EncryptedStore {
  private static instance: EncryptedStore;
  private currentLedger: LedgerDocument | null = null;
  private currentLedgerId: string | null = null;
  private saveTimeout: NodeJS.Timeout | null = null;
  
  private constructor() {}

  static getInstance(): EncryptedStore {
    if (!EncryptedStore.instance) {
      EncryptedStore.instance = new EncryptedStore();
    }
    return EncryptedStore.instance;
  }

  // Create a new encrypted ledger
  async createLedger(passphrase: string, householdName: string, ownerName: string): Promise<boolean> {
    try {
      const keyManager = await getKeyManager();
      const cryptoBox = await getCryptoBox();
      const db = getDatabase();
      
      // Generate new encryption keys
      const dek = keyManager.generateDataEncryptionKey();
      const salt = keyManager.generateSalt();
      const kek = await keyManager.deriveKeyEncryptionKey(passphrase, salt);
      const encDEK = keyManager.encryptDataEncryptionKey(dek, kek);
      
      // Create ledger ID and initial data
      const ledgerId = crypto.randomUUID();
      const household: Household = {
        id: crypto.randomUUID(),
        name: householdName,
        members: []
      };
      
      const owner: Member = {
        id: crypto.randomUUID(),
        role: 'owner',
        display: ownerName
      };
      
      household.members.push(owner);
      
      // Store metadata (unencrypted ledger info)
      const metadata: LedgerMetadata = {
        id: ledgerId,
        salt,
        encDEK,
        householdName,
        createdAt: Date.now(),
        lastAccessed: Date.now(),
        version: 1
      };
      
      await db.storeLedgerMetadata(metadata);
      
      // Set the DEK in memory and initialize the ledger document
      keyManager.setDataEncryptionKey(dek);
      
      this.currentLedger = new LedgerDocument();
      this.currentLedger.initializeNewLedger(household, owner);
      this.currentLedgerId = ledgerId;
      
      // Save the initial document
      await this.saveLedger();
      
      return true;
    } catch (error) {
      console.error('Failed to create ledger:', error);
      return false;
    }
  }

  // Unlock an existing ledger with passphrase
  async unlockLedger(passphrase: string, ledgerId?: string): Promise<boolean> {
    try {
      const keyManager = await getKeyManager();
      const cryptoBox = await getCryptoBox();
      const db = getDatabase();
      
      // If no ledger ID provided, get the most recently accessed one
      if (!ledgerId) {
        const ledgers = await db.listLedgers();
        if (ledgers.length === 0) {
          throw new Error('No ledgers found');
        }
        ledgerId = ledgers[0].id;
      }
      
      // Get ledger metadata
      const metadata = await db.getLedgerMetadata(ledgerId);
      if (!metadata) {
        throw new Error('Ledger not found');
      }
      
      // Derive KEK and decrypt DEK
      const kek = await keyManager.deriveKeyEncryptionKey(passphrase, metadata.salt);
      const dek = keyManager.decryptDataEncryptionKey(metadata.encDEK, kek);
      
      if (!dek) {
        throw new Error('Invalid passphrase');
      }
      
      // Set the DEK in memory
      keyManager.setDataEncryptionKey(dek);
      
      // Load the ledger document
      const chunks = await db.getDocumentChunks(ledgerId);
      
      this.currentLedger = new LedgerDocument();
      this.currentLedgerId = ledgerId;
      
      if (chunks.length > 0) {
        // Decrypt and apply each chunk
        for (const chunk of chunks) {
          const decryptedChunk = await cryptoBox.decrypt(chunk.bytes);
          this.currentLedger.applyUpdate(decryptedChunk);
        }
      }
      
      return true;
    } catch (error) {
      console.error('Failed to unlock ledger:', error);
      return false;
    }
  }

  // Lock the ledger (clear from memory)
  lock(): void {
    const keyManager = getKeyManager();
    keyManager.then(km => km.lock());
    
    this.currentLedger = null;
    this.currentLedgerId = null;
    
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
  }

  // Check if the ledger is unlocked
  isUnlocked(): boolean {
    return this.currentLedger !== null && this.currentLedgerId !== null;
  }

  // Get available ledgers for selection
  async getAvailableLedgers(): Promise<Pick<LedgerMetadata, 'id' | 'householdName' | 'createdAt' | 'lastAccessed'>[]> {
    const db = getDatabase();
    return await db.listLedgers();
  }

  // Save the current ledger to IndexedDB
  private async saveLedger(): Promise<void> {
    if (!this.currentLedger || !this.currentLedgerId) {
      throw new Error('No ledger to save');
    }

    const cryptoBox = await getCryptoBox();
    const db = getDatabase();
    
    // Serialize the Yjs document
    const update = this.currentLedger.serializeToUpdate();
    
    // Encrypt the serialized document
    const encryptedUpdate = await cryptoBox.encrypt(update);
    
    // Store as a single chunk (could be split for large documents)
    await db.storeDocumentChunks(this.currentLedgerId, [encryptedUpdate]);
    
    // Optionally create a snapshot for backup/sync
    await db.storeSnapshot(this.currentLedgerId, encryptedUpdate);
  }

  // Schedule automatic save (debounced)
  private scheduleSave(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    
    this.saveTimeout = setTimeout(async () => {
      try {
        await this.saveLedger();
      } catch (error) {
        console.error('Failed to auto-save ledger:', error);
      }
    }, 1000); // Save 1 second after last change
  }

  // Household operations
  getHousehold(): Household | null {
    if (!this.currentLedger) return null;
    return this.currentLedger.getHousehold();
  }

  // Account operations
  getAccounts(): Account[] {
    if (!this.currentLedger) return [];
    return this.currentLedger.getAccounts();
  }

  addAccount(account: Omit<Account, 'id'>): void {
    if (!this.currentLedger) throw new Error('Ledger not unlocked');
    
    const fullAccount: Account = {
      ...account,
      id: crypto.randomUUID()
    };
    
    const household = this.currentLedger.getHousehold();
    const currentUser = household?.members[0]; // Simplified: use first member as current user
    
    if (currentUser) {
      this.currentLedger.addAccount(fullAccount, currentUser.id);
      this.scheduleSave();
    }
  }

  // Category operations
  getCategories(): Category[] {
    if (!this.currentLedger) return [];
    return this.currentLedger.getCategories();
  }

  addCategory(category: Omit<Category, 'id'>): void {
    if (!this.currentLedger) throw new Error('Ledger not unlocked');
    
    const fullCategory: Category = {
      ...category,
      id: crypto.randomUUID()
    };
    
    const household = this.currentLedger.getHousehold();
    const currentUser = household?.members[0];
    
    if (currentUser) {
      this.currentLedger.addCategory(fullCategory, currentUser.id);
      this.scheduleSave();
    }
  }

  // Transaction operations
  getTransactions(): Transaction[] {
    if (!this.currentLedger) return [];
    return this.currentLedger.getTransactions();
  }

  addTransaction(transaction: Omit<Transaction, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>): void {
    if (!this.currentLedger) throw new Error('Ledger not unlocked');
    
    const household = this.currentLedger.getHousehold();
    const currentUser = household?.members[0];
    
    if (!currentUser) throw new Error('No current user');
    
    const now = new Date().toISOString();
    const fullTransaction: Transaction = {
      ...transaction,
      id: crypto.randomUUID(),
      createdBy: currentUser.id,
      createdAt: now,
      updatedAt: now
    };
    
    this.currentLedger.addTransaction(fullTransaction);
    this.scheduleSave();
  }

  // Budget allocation operations
  getBudgetAllocations(): BudgetAllocation[] {
    if (!this.currentLedger) return [];
    return this.currentLedger.getBudgetAllocations();
  }

  updateBudgetAllocation(categoryId: string, month: string, allocated: number): void {
    if (!this.currentLedger) throw new Error('Ledger not unlocked');
    
    const household = this.currentLedger.getHousehold();
    const currentUser = household?.members[0];
    
    if (currentUser) {
      this.currentLedger.updateBudgetAllocation(categoryId, month, allocated, currentUser.id);
      this.scheduleSave();
    }
  }

  // Schedule operations
  getSchedules(): Schedule[] {
    if (!this.currentLedger) return [];
    return this.currentLedger.getSchedules();
  }

  addSchedule(schedule: Omit<Schedule, 'id'>): void {
    if (!this.currentLedger) throw new Error('Ledger not unlocked');
    
    const fullSchedule: Schedule = {
      ...schedule,
      id: crypto.randomUUID()
    };
    
    const household = this.currentLedger.getHousehold();
    const currentUser = household?.members[0];
    
    if (currentUser) {
      this.currentLedger.addSchedule(fullSchedule, currentUser.id);
      this.scheduleSave();
    }
  }

  updateSchedule(scheduleId: string, updates: Partial<Schedule>): void {
    if (!this.currentLedger) throw new Error('Ledger not unlocked');
    
    const household = this.currentLedger.getHousehold();
    const currentUser = household?.members[0];
    
    if (currentUser) {
      this.currentLedger.updateSchedule(scheduleId, updates, currentUser.id);
      this.scheduleSave();
    }
  }

  deleteSchedule(scheduleId: string): void {
    if (!this.currentLedger) throw new Error('Ledger not unlocked');
    
    const household = this.currentLedger.getHousehold();
    const currentUser = household?.members[0];
    
    if (currentUser) {
      this.currentLedger.deleteSchedule(scheduleId, currentUser.id);
      this.scheduleSave();
    }
  }

  // Export ledger as JSON (for backup/sync)
  async exportLedger(): Promise<string> {
    if (!this.currentLedger) throw new Error('Ledger not unlocked');
    
    const data = {
      household: this.getHousehold(),
      accounts: this.getAccounts(),
      categories: this.getCategories(),
      transactions: this.getTransactions(),
      budgetAllocations: this.getBudgetAllocations(),
      schedules: this.getSchedules(),
      exportedAt: new Date().toISOString(),
      version: 1
    };
    
    return JSON.stringify(data, null, 2);
  }

  // Import ledger from JSON (merge with existing)
  async importLedger(jsonData: string, userId: UUID): Promise<void> {
    if (!this.currentLedger) throw new Error('Ledger not unlocked');
    
    try {
      const data = JSON.parse(jsonData);
      
      // Import accounts
      if (data.accounts) {
        data.accounts.forEach((account: Account) => {
          this.currentLedger!.addAccount(account, userId);
        });
      }
      
      // Import categories
      if (data.categories) {
        data.categories.forEach((category: Category) => {
          this.currentLedger!.addCategory(category, userId);
        });
      }
      
      // Import transactions
      if (data.transactions) {
        data.transactions.forEach((transaction: Transaction) => {
          this.currentLedger!.addTransaction(transaction);
        });
      }
      
      // Import budget allocations
      if (data.budgetAllocations) {
        data.budgetAllocations.forEach((allocation: BudgetAllocation) => {
          this.currentLedger!.updateBudgetAllocation(
            allocation.categoryId, 
            allocation.month, 
            allocation.allocated, 
            userId
          );
        });
      }
      
      this.scheduleSave();
    } catch (error) {
      throw new Error(`Failed to import ledger: ${error}`);
    }
  }

  // Force save now (for explicit save operations)
  async saveNow(): Promise<void> {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
    await this.saveLedger();
  }
}